import { Flex, Group, Stack, Text } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { GovEvaluationAuthorMinimalStandard } from '@/module/GovEvaluation/components/GovEvaluationAuthorMinimalStandard';

import { Content, DateText } from '@/core/components';

import { RoleType, useCurrentUser } from '@feature/security';

import { getGovAuthorEvaluationOptions } from './/model/GovEvaluationQuery';
import { GovAuthorEvaluationValidationAction } from './Validation/GovAuthorEvaluationValidationAction';
import { GovAuthorEvaluationValidationHistory } from './Validation/GovAuthorEvaluationValidationHistory';
import { GovEvaluationAuthorAnnex } from './components/GovEvaluationAuthorAnnex';
import { GovEvaluationAuthorHIndex } from './components/GovEvaluationAuthorHIndex';
import { GovEvaluationReportZipButton } from './components/GovEvaluationReportZipButton';
import { GovEvaluationUserData } from './components/GovEvaluationUserData';
import { StartGovEvaluationButton } from './components/StartGovEvaluationButton';
import { GovEvaluation } from './model/GovEvaluationTypes';

interface AuthorEvaluationOverviewProps {
  evaluation: GovEvaluation;
  canStartEvaluation: boolean;
  userId: number;

  canVerifyEvaluation?: boolean;
  currentRole: RoleType;
}

export function GovAuthorEvaluationOverview(props: AuthorEvaluationOverviewProps) {
  const { evaluation, userId } = props;
  const { t } = useTranslation();
  const { data } = useSuspenseQuery(getGovAuthorEvaluationOptions(evaluation.id, userId));
  const { currentRole, facultyDomain } = useCurrentUser();
  const canEdit = (evaluation.open || !!data?.changeRequested) && !data?.completed && currentRole === 'AUTHOR';

  if (!data) {
    return (
      <Stack>
        <Content type="full">
          <Flex
            align="center"
            justify="space-between"
          >
            <div />
            <StartGovEvaluationButton evaluationId={evaluation.id} />
          </Flex>
        </Content>
        <Content type="full">
          {data == null && (
            <Text
              fw="500"
              c="dimmed"
              fs="italic"
            >
              {t('govEvaluation.reportingNotStartedYet')}
            </Text>
          )}
        </Content>
      </Stack>
    );
  }

  return (
    <Stack>
      <Content type="full">
        <Flex justify="space-between">
          {data.startedAt && (
            <Flex>
              <Text mr="10px">{t('govEvaluation.startedAt')}</Text>
              <DateText
                showAs="date-time"
                date={data.startedAt}
              />
            </Flex>
          )}
          {data.completedAt && (
            <Flex>
              <Text mr="10px">{t('govEvaluation.completedAt')}</Text>
              <DateText
                showAs="date-time"
                date={data.completedAt}
              />
            </Flex>
          )}
        </Flex>
        <Group>
          <GovEvaluationReportZipButton
            evaluationId={evaluation.id}
            authorId={userId}
          />
        </Group>
      </Content>
      <Content type="full">
        <GovEvaluationUserData
          data={data}
          canEdit={canEdit}
        />
      </Content>
      <Content type="full">
        <Text fw="500">{t('govEvaluation.minimalStandard')}</Text>
        {data.jobTitle != null && data.domain != null ? (
          <GovEvaluationAuthorMinimalStandard
            evaluationId={evaluation.id}
            data={data.minimalStandard}
            jobTitle={data.jobTitle}
            authorId={userId}
            canEdit={canEdit}
          />
        ) : (
          <Text
            fs="italic"
            c="red"
          >
            Pentru a putea urca fișa CNATCU în platformă este necesară completarea secțiunii "Date generale"
          </Text>
        )}
      </Content>
      <Content type="full">
        <Text fw="500">{t('govEvaluation.hIndex')}</Text>
        <Stack>
          <GovEvaluationAuthorHIndex
            evaluationId={evaluation.id}
            data={data.wosHIndex}
            hIndexType="WOS"
            authorId={userId}
            canEdit={canEdit}
          />
          <GovEvaluationAuthorHIndex
            evaluationId={evaluation.id}
            data={data.scopusHIndex}
            hIndexType="SCOPUS"
            canEdit={canEdit}
            authorId={userId}
          />
          <GovEvaluationAuthorHIndex
            evaluationId={evaluation.id}
            data={data.googleScholarHIndex}
            hIndexType="GOOGLE_SCHOLAR"
            authorId={userId}
            canEdit={canEdit}
          />
        </Stack>
      </Content>
      <Content type="full">
        <Text fw="500">{t('govEvaluation.annexList')}</Text>
        <GovEvaluationAuthorAnnex
          data={data}
          hideOptionals={facultyDomain === 'MEDICAL'}
          canEdit={canEdit}
          authorId={userId}
          evaluationId={evaluation.id}
        />
      </Content>
      <Content type="full">
        <GovAuthorEvaluationValidationAction itemId={data.id} />
        <GovAuthorEvaluationValidationHistory itemId={data.id} />
      </Content>
    </Stack>
  );
}
